<template>
  <header id="navigation" data-aos="fade-down" style="">
    <!-- Download Section for Mobile Only -->
    <div v-if="showDownloadBar" class="download-card-wrapper">
      <div class="download-card">
        <a
          href="https://play.google.com/store/apps/details?id=tz.co.eafrica.abood"
        >
          <img
            src="@/assets/images/google-play-icon-transparent-6.png"
            alt="Google Play"
            class="download-img"
          />
        </a>
        <a
          href="https://apps.apple.com/us/app/abood-bus-passenger-app/id1672754974"
        >
          <img
            src="@/assets/images/app-store-icon-transparent-6.png"
            alt="App Store"
            class="download-img"
          />
        </a>
      </div>
    </div>

    <nav class="navbar main-nav navbar-expand-lg navbar-light bg-light">
      <div class="container-fluid">
        <router-link class="navbar-brand" to="/#home">
          <img src="@/assets/images/abood_icons.svg" alt="Brand Logo" />
        </router-link>
        <button
          class="navbar-toggler"
          type="button"
          @click="toggleNavbar"
          :aria-expanded="isNavbarOpen.toString()"
          aria-label="Toggle navigation"
        >
          <span class="navbar-toggler-icon"></span>
        </button>

        <!-- Collapsible Menu -->
        <div
          class="collapse navbar-collapse navbar-right"
          :class="{ show: isNavbarOpen, 'menu-open': isNavbarOpen }"
          id="navbarSupportedContent"
        >
          <ul class="navbar-nav ml-auto">
            <li class="nav-item active">
              <router-link class="nav-link" to="/#home">{{
                $t("navbar.home")
              }}</router-link>
            </li>
            <li class="nav-item" v-if="!isMobile">
              <router-link class="nav-link" to="/#about-us">{{
                $t("navbar.about")
              }}</router-link>
            </li>
            <li class="nav-item" v-if="!isMobile">
              <router-link class="nav-link" to="/#our-trip">{{
                $t("navbar.routes")
              }}</router-link>
            </li>
            <li class="nav-item" v-if="!isMobile">
              <router-link class="nav-link" to="/#our-services">{{
                $t("navbar.services")
              }}</router-link>
            </li>

            <li class="nav-item" v-if="!isMobile">
              <router-link class="nav-link" to="/#contact">{{
                $t("navbar.contact")
              }}</router-link>
            </li>
            <li v-if="!isLoggedIn" class="nav-item">
              <a class="nav-link" to="#" @click.prevent="$emit('show-login')">
                {{ $t("navbar.login") }}
              </a>
            </li>
            <li class="nav-item">
              <LanguageSwitch @dropdown-opened="closeAllDropdowns" />
            </li>
            <!-- Account Dropdown - Shows for both logged in and guests -->
            <li
              class="nav-item dropdown account-dropdown"
              style="padding-right: 15px"
            >
              <a
                class="nav-link dropdown-toggle d-flex align-items-center"
                href="#"
                role="button"
                ref="accountDropdownToggle"
                @click.prevent="toggleAccountDropdown"
                @mouseenter="handleMouseEnter"
                @mouseleave="handleMouseLeave"
              >
                <img
                  :src="getUserAvatar()"
                  alt="Profile"
                  class="rounded-circle d-flex align-items-center"
                  style="
                    width: 30px;
                    height: 30px;
                    margin-right: 8px;
                    object-fit: cover;
                  "
                  @error="handleImageError"
                />
              </a>
              <ul
                class="dropdown-menu dropdown-menu-end"
                ref="accountDropdownMenu"
                aria-labelledby="navbarDropdown"
                style="min-width: 180px; right: 15px"
                @mouseenter="handleMouseEnter"
                @mouseleave="handleMouseLeave"
              >
                <!-- Show user profile only if logged in -->
                <li v-if="isLoggedIn">
                  <router-link class="dropdown-item" to="/user-profile" @click="closeDropdown">
                    <i class="bi bi-person-fill me-2"></i>
                    {{ currentUser?.name || "User" }}
                  </router-link>
                </li>
                <!-- Always show Tickets option for both logged in and guests -->
                <li>
                  <router-link class="dropdown-item" to="/ticket" @click="closeDropdown">
                    <i class="bi bi-ticket-perforated-fill me-2"></i> Tickets
                  </router-link>
                </li>

                <!-- Show logout only if logged in -->
                <template v-if="isLoggedIn">
                  <li><hr class="dropdown-divider" /></li>
                  <li>
                    <a
                      class="dropdown-item"
                      href="#"
                      @click.prevent="handleLogout"
                    >
                      <i class="bi bi-box-arrow-right me-2"></i> Logout
                    </a>
                  </li>
                </template>
                <!-- Show login option for guests -->
                <template v-else>
                  <li><hr class="dropdown-divider" /></li>
                  <li>
                    <a
                      class="dropdown-item"
                      href="#"
                      @click.prevent="$emit('show-login'); closeDropdown()"
                    >
                      <i class="bi bi-box-arrow-in-right me-2"></i> Login
                    </a>
                  </li>
                </template>
              </ul>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  </header>
</template>

<script>
import { computed, ref, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import { Dropdown } from "bootstrap";
import NavbarUtils from "@/utils/navbarUtils";
import LanguageSwitch from "./LanguageSwitch.vue";
import { USER_URL } from "@/config/config";
import defaultProfileImage from '@/assets/images/profile-user.png';

import axios from "axios";

const axiosInstance = axios.create({
  baseURL: USER_URL,
});

export default {
  name: "AppNavbar",
  components: { LanguageSwitch },
  setup() {
    const store = useStore();
    const isLoggedIn = computed(() => store.getters.isAuthenticated);
    const currentUser = computed(() => store.getters.currentUser);
    const authToken = computed(() => store.getters.authToken);

    // Account dropdown references
    const accountDropdownToggle = ref(null);
    const accountDropdownMenu = ref(null);
    let accountDropdownInstance = null;
    let hoverTimeout = null;

    const getUserAvatar = () => {
      if (isLoggedIn.value && currentUser.value?.user_image_url) {
        return currentUser.value.user_image_url;
      }
      return defaultProfileImage;
    };

    const handleImageError = (event) => {
      event.target.src = defaultProfileImage;
    };

    const closeAllDropdowns = () => {
      if (accountDropdownInstance) {
        accountDropdownInstance.hide();
      }
      document.dispatchEvent(new CustomEvent('close-language-dropdown'));
    };

    const toggleAccountDropdown = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (!accountDropdownInstance) return;
      
      document.dispatchEvent(new CustomEvent('close-language-dropdown'));
      
      const isShown = accountDropdownMenu.value?.classList.contains('show');
      
      if (isShown) {
        accountDropdownInstance.hide();
      } else {
        accountDropdownInstance.show();
      }
    };

    const closeDropdown = () => {
      if (accountDropdownInstance) {
        accountDropdownInstance.hide();
      }
    };

    const handleMouseEnter = () => {
      if (window.innerWidth >= 769) {
        clearTimeout(hoverTimeout);
        if (accountDropdownInstance) {
          document.dispatchEvent(new CustomEvent('close-language-dropdown'));
          accountDropdownInstance.show();
        }
      }
    };

    const handleMouseLeave = () => {
      if (window.innerWidth >= 769) {
        hoverTimeout = setTimeout(() => {
          if (accountDropdownInstance) {
            accountDropdownInstance.hide();
          }
        }, 200);
      }
    };

    const handleClickOutside = (event) => {
      const isAccountDropdown = accountDropdownToggle.value?.contains(event.target) || 
                               accountDropdownMenu.value?.contains(event.target);
      
      if (!isAccountDropdown) {
        closeDropdown();
      }
    };

    onMounted(() => {
      if (accountDropdownToggle.value) {
        accountDropdownInstance = new Dropdown(accountDropdownToggle.value, {
          autoClose: true
        });
        
        accountDropdownToggle.value.addEventListener('touchstart', (e) => {
          e.preventDefault();
          toggleAccountDropdown(e);
        });
      }

      document.addEventListener('close-language-dropdown', closeDropdown);
      document.addEventListener('click', handleClickOutside);
      
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          closeDropdown();
          document.dispatchEvent(new CustomEvent('close-language-dropdown'));
        }
      });
    });

    onUnmounted(() => {
      if (accountDropdownInstance) {
        accountDropdownInstance.dispose();
      }
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
      document.removeEventListener('close-language-dropdown', closeDropdown);
      document.removeEventListener('click', handleClickOutside);
    });

    return {
      isLoggedIn,
      currentUser,
      authToken,
      accountDropdownToggle,
      accountDropdownMenu,
      toggleAccountDropdown,
      closeDropdown,
      closeAllDropdowns,
      handleMouseEnter,
      handleMouseLeave,
      getUserAvatar,
      handleImageError,
    };
  },
  data() {
    return {
      topMenuHeight: 0,
      size_li: 0,
      x: 4,
      isNavbarOpen: false,
      isDesktop: true,
      isMobile: false,
    };
  },

  computed: {
    showDownloadBar() {
      return this.$route.name === "Home" && this.isMobile;
    },
  },

  mounted() {
    NavbarUtils.handlePreloader();
    NavbarUtils.initParallax();
    NavbarUtils.handleNavigationScroll(this.topMenuHeight);
    NavbarUtils.hideMobileMenu();
    NavbarUtils.handleNavBackground();
    NavbarUtils.handleSearch();
    NavbarUtils.setSliderHeight();
    NavbarUtils.loadMoreItems(this.size_li, this.x);
    this.isuserLogin();
    window.addEventListener("storage", () => {
      this.token = localStorage.getItem("auth_token");
    });
    this.checkScreenSize();
    window.addEventListener("resize", this.checkScreenSize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.checkScreenSize);
  },
  methods: {
    toggleNavbar() {
      this.isNavbarOpen = !this.isNavbarOpen;
    },
    checkScreenSize() {
      this.isDesktop = window.matchMedia("(min-width: 1024px)").matches;
      this.isMobile = window.matchMedia("(max-width: 767px)").matches;
    },

    isuserLogin() {
      let token = localStorage.getItem("auth_token");
      if (token) {
        this.isLoggedIn = true;
      } else {
        this.isLoggedIn = false;
      }
    },

    async handleLogout() {
      try {
        const response = await axiosInstance.post(
          "customer/logout",
          {},
          {
            headers: { Authorization: `Bearer ${this.authToken}` },
          }
        );
        const data = response.data;

        if (data.success === true) {
          this.$store.dispatch("logout");
          this.$router.push("/");
          this.closeDropdown();
        }
      } catch (err) {
        alert("Invalid credentials, please try again.");
      }
    },
  },
};
</script>

<style scoped>
.navbar-toggler:focus {
  outline: none;
  box-shadow: none;
}

.navbar-toggler {
  border: none;
  background-color: transparent;
}

/* Account dropdown styles */
.account-dropdown .dropdown-item {
  cursor: pointer;
  padding: 8px 16px;
}

.account-dropdown .dropdown-item i {
  font-size: 1.1rem;
}

.account-dropdown .dropdown-item:hover {
  background-color: #f0f0f0;
}

.account-dropdown .dropdown-divider {
  margin: 0.3rem 0;
}

/* Mobile-friendly dropdown styles */
@media (max-width: 768px) {
  .account-dropdown .dropdown-menu {
    position: absolute;
    right: 0;
    left: auto;
    margin-top: 0.5rem;
  }
  
  .account-dropdown .dropdown-toggle::after {
    display: inline-block;
    margin-left: 0.255em;
    vertical-align: 0.255em;
    content: "";
    border-top: 0.3em solid;
    border-right: 0.3em solid transparent;
    border-bottom: 0;
    border-left: 0.3em solid transparent;
  }
}

/* Disable hover on mobile */
@media (max-width: 768px) {
  .account-dropdown:hover .dropdown-menu {
    display: none;
  }
}

/* Download Card Styles */
.download-card-wrapper {
  width: 100%;
  background: #f8f9fa;
  padding: 0.5rem 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.download-card {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  max-width: 400px;
  margin: 0 auto;
}

.download-img {
  width: 150px;
  max-width: 100%;
  height: auto;
}

@media (min-width: 768px) {
  .download-card-wrapper {
    display: none;
  }
}
</style>